import clsx from 'clsx'
import React, { ComponentProps, forwardRef, Fragment, ReactNode, useState } from 'react'

export interface TextAreaProps extends ComponentProps<'textarea'> {
  label?: ReactNode
  errorMessage?: ReactNode
  inputClass?: string
  iconClass?: string
  innerClass?: string
  labelClass?: string
  description?: ReactNode
  collapseDescriptionArea?: boolean
  hideLabel?: boolean
  resizeable?: boolean
  value?: string
  hideExtraLine?: boolean
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      className = '',
      value,
      onChange,
      label,
      id,
      errorMessage,
      iconClass = 'text-gray-400',
      inputClass = '',
      innerClass = '',
      labelClass = '',
      description,
      collapseDescriptionArea = false,
      hideLabel = false,
      resizeable = false,
      hideExtraLine = false,
      rows,
      ...props
    },
    ref
  ) => {
    const autoExpand = typeof rows !== 'number'
    const [touched, setTouched] = useState(false)

    const requiredAndBlank = !value && touched && props.required

    const errorState = !!errorMessage || requiredAndBlank

    return (
      <div className={`${className}`}>
        {label && (
          <label
            htmlFor={id}
            className={clsx('block text-base font-medium text-gray-700', labelClass, { 'sr-only': hideLabel })}
          >
            {label}
            {props.required && (
              <span className={clsx({ 'text-emerald-400': !requiredAndBlank, 'text-red-400': requiredAndBlank })}>
                {' '}
                *
              </span>
            )}
          </label>
        )}
        <div className={clsx(innerClass, 'relative rounded-md shadow-sm', { 'mt-1': !!label })}>
          {autoExpand && (
            <div
              className={clsx(
                'invisible px-3 py-2 break-words border border-transparent',
                hideExtraLine ? 'min-h-10' : 'min-h-16'
              )}
            >
              {value?.split('\n').map((s, i, a) => (
                <Fragment key={i}>
                  {s}
                  {!(hideExtraLine && i === a.length - 1 && s) && <br />}
                </Fragment>
              ))}
            </div>
          )}
          <textarea
            id={id}
            className={clsx(
              'block w-full rounded-md focus:outline-none disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-50 disabled:text-gray-500 placeholder:text-gray-400',
              inputClass,
              {
                'resize-y': resizeable,
                'resize-none': !resizeable,
                'border-red-300 text-red-900 placeholder-red-300 focus:border-red-500 focus:ring-red-500': errorState,
                'border-primary-300 text-primary-900 placeholder-primary-300 focus:border-primary-500 focus:ring-primary-500':
                  !errorState,
                'absolute inset-0': autoExpand,
              }
            )}
            value={value}
            onChange={(e) => {
              if (!touched) {
                setTouched(true)
              }
              onChange?.(e)
            }}
            aria-invalid={errorMessage ? 'true' : 'false'}
            aria-describedby={`${id}-description`}
            ref={ref}
            rows={rows}
            {...props}
          />
        </div>
        {!collapseDescriptionArea && (
          <p
            className={clsx('mt-1 text-sm', {
              'text-red-600': errorState,
              'text-gray-500': description && !errorState,
              'h-5': !description && !errorState && !collapseDescriptionArea,
            })}
            id={`${id}-description`}
          >
            {errorMessage || (requiredAndBlank && 'This field is required.') || description}
          </p>
        )}
      </div>
    )
  }
)
